import React, { useState } from 'react';
import { Zap, Loader2, CheckCircle, XCircle, Clock, Activity } from 'lucide-react';
import { api } from '../services/api';
import toast from 'react-hot-toast';

interface TestResult {
  endpoint: string;
  method: string;
  totalRequests?: number;
  successfulRequests?: number;
  failedRequests?: number;
  errorRate?: string;
  avgDuration?: string;
  minDuration?: string;
  maxDuration?: string;
  requestsPerSecond?: string;
  error?: string;
}

interface TestSummary {
  totalRequests: number;
  totalSuccessful: number;
  totalFailed: number;
  successRate: string;
  avgResponseTime: string;
  maxResponseTime: string;
  timestamp: string;
}

const Testes: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<TestResult[]>([]);
  const [summary, setSummary] = useState<TestSummary | null>(null);
  const [progress, setProgress] = useState<string>('');
  const [config, setConfig] = useState({
    requestsPerEndpoint: 50,
    concurrentRequests: 10
  });

  const executeTests = async () => {
    setLoading(true);
    setResults([]);
    setSummary(null);
    setProgress('Iniciando testes de carga...');

    try {
      // Timeout maior para testes de carga (5 minutos)
      const response = await api.post('/testes/carga', {
        requestsPerEndpoint: config.requestsPerEndpoint,
        concurrentRequests: config.concurrentRequests
      }, {
        timeout: 300000 // 5 minutos
      });

      if (response.data.success) {
        setResults(response.data.results);
        setSummary(response.data.summary);
        setProgress('');
        toast.success('Teste de carga executado com sucesso!');
      } else {
        throw new Error(response.data.message || 'Erro ao executar teste');
      }
    } catch (error: any) {
      console.error('Erro ao executar teste:', error);
      setProgress('');
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('Timeout: O teste está demorando mais que o esperado. Tente reduzir o número de requisições.');
      } else {
        toast.error(error.response?.data?.message || 'Erro ao executar teste de carga');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (errorRate: string | undefined) => {
    if (!errorRate) return 'text-gray-500';
    const rate = parseFloat(errorRate);
    if (rate === 0) return 'text-green-600';
    if (rate < 1) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getResponseTimeColor = (time: string | undefined) => {
    if (!time) return 'text-gray-500';
    const ms = parseFloat(time);
    if (ms < 100) return 'text-green-600';
    if (ms < 300) return 'text-blue-600';
    if (ms < 1000) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <Zap className="mr-2 h-6 w-6 text-blue-600" />
              Testes de Carga
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Execute testes de carga na API para avaliar performance
            </p>
          </div>
        </div>
      </div>

      {/* Configuração */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Configuração do Teste</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Requisições por Endpoint
            </label>
            <input
              type="number"
              min="10"
              max="500"
              value={config.requestsPerEndpoint}
              onChange={(e) => setConfig({ ...config, requestsPerEndpoint: parseInt(e.target.value) || 50 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Requisições Simultâneas
            </label>
            <input
              type="number"
              min="1"
              max="50"
              value={config.concurrentRequests}
              onChange={(e) => setConfig({ ...config, concurrentRequests: parseInt(e.target.value) || 10 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        <button
          onClick={executeTests}
          disabled={loading}
          className="mt-4 w-full md:w-auto px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Executando testes...
            </>
          ) : (
            <>
              <Zap className="mr-2 h-4 w-4" />
              Executar Teste de Carga
            </>
          )}
        </button>
        {loading && progress && (
          <p className="mt-2 text-sm text-gray-600">{progress}</p>
        )}
      </div>

      {/* Resumo */}
      {summary && (
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Activity className="mr-2 h-5 w-5 text-blue-600" />
            Resumo Geral
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Total de Requisições</p>
              <p className="text-2xl font-bold text-blue-600">{summary.totalRequests}</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Bem-sucedidas</p>
              <p className="text-2xl font-bold text-green-600">{summary.totalSuccessful}</p>
              <p className="text-xs text-gray-500 mt-1">{summary.successRate}%</p>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Tempo Médio</p>
              <p className="text-2xl font-bold text-yellow-600">{summary.avgResponseTime}ms</p>
            </div>
            <div className="bg-red-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Tempo Máximo</p>
              <p className="text-2xl font-bold text-red-600">{summary.maxResponseTime}ms</p>
            </div>
          </div>
          <p className="mt-4 text-xs text-gray-500">
            Executado em: {new Date(summary.timestamp).toLocaleString('pt-BR')}
          </p>
        </div>
      )}

      {/* Resultados Detalhados */}
      {results.length > 0 && (
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Resultados por Endpoint</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Endpoint
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Req/s
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tempo Médio (ms)
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tempo Máx (ms)
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Taxa de Erro (%)
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {results.map((result, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {result.method} {result.endpoint}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {result.requestsPerSecond || '-'}
                    </td>
                    <td className={`px-4 py-3 whitespace-nowrap text-sm font-medium ${getResponseTimeColor(result.avgDuration)}`}>
                      {result.avgDuration || '-'}
                    </td>
                    <td className={`px-4 py-3 whitespace-nowrap text-sm font-medium ${getResponseTimeColor(result.maxDuration)}`}>
                      {result.maxDuration || '-'}
                    </td>
                    <td className={`px-4 py-3 whitespace-nowrap text-sm font-medium ${getStatusColor(result.errorRate)}`}>
                      {result.errorRate || '0.0'}%
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {result.error ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <XCircle className="mr-1 h-3 w-3" />
                          Erro
                        </span>
                      ) : parseFloat(result.errorRate || '0') === 0 ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="mr-1 h-3 w-3" />
                          OK
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          <Clock className="mr-1 h-3 w-3" />
                          Atenção
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Testes;
